import network from '@/services/network';
import {
  GoogleCalendarEvent,
  FreeBusySlot,
  GoogleStatus,
  GoogleTeamMember,
  ExternalCalendarEvent,
} from '@/types';
import { ErrorResponse } from '@/utils';

const getAuthUrl = async (): Promise<string> => {
  try {
    const { data } = await network.get<{ url: string }>('/google/auth-url');
    return data.url;
  } catch (error) { throw new ErrorResponse(error); }
};

const getStatus = async (): Promise<GoogleStatus> => {
  try {
    const { data } = await network.get<GoogleStatus>('/google/status');
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

const getEvents = async (start: string, end: string): Promise<GoogleCalendarEvent[]> => {
  try {
    const { data } = await network.get<{ data: GoogleCalendarEvent[] }>('/google/events', {
      params: { start, end },
    });
    return data.data;
  } catch (error) { throw new ErrorResponse(error); }
};

const getFreeBusy = async (start: string, end: string): Promise<FreeBusySlot[]> => {
  try {
    const { data } = await network.get<{ data: FreeBusySlot[] }>('/google/freebusy', {
      params: { start, end },
    });
    return data.data;
  } catch (error) { throw new ErrorResponse(error); }
};

const createEvent = async (payload: {
  title: string;
  start: string;
  end: string;
  description?: string;
}): Promise<{ id: string; htmlLink: string }> => {
  try {
    const { data } = await network.post<{ data: { id: string; htmlLink: string } }>('/google/events', payload);
    return data.data;
  } catch (error) { throw new ErrorResponse(error); }
};

const disconnect = async (): Promise<void> => {
  try {
    await network.delete('/google/disconnect');
  } catch (error) { throw new ErrorResponse(error); }
};

const getTeamConnected = async (): Promise<{ members: GoogleTeamMember[]; unconnectedVps: number }> => {
  try {
    const { data } = await network.get<{ data: GoogleTeamMember[]; meta?: { unconnected_vps: number } }>('/google/team-connected');
    return { members: data.data, unconnectedVps: data.meta?.unconnected_vps ?? 0 };
  } catch (error) { throw new ErrorResponse(error); }
};

const getTeamEvents = async (userId: number, start: string, end: string): Promise<GoogleCalendarEvent[]> => {
  try {
    const { data } = await network.get<{ data: GoogleCalendarEvent[] }>('/google/team-events', {
      params: { user_id: userId, start, end },
    });
    return data.data;
  } catch (error) { throw new ErrorResponse(error); }
};

const triggerSync = async (): Promise<{ message: string; events_synced: number }> => {
  try {
    const { data } = await network.post<{ message: string; events_synced: number }>('/google/sync');
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

const getExternalEvents = async (from: string, to: string): Promise<ExternalCalendarEvent[]> => {
  try {
    const { data } = await network.get<{ data: ExternalCalendarEvent[] }>('/google/external-events', {
      params: { from, to },
    });
    return data.data;
  } catch (error) { throw new ErrorResponse(error); }
};

const updateEvent = async (eventId: string, payload: {
  title: string;
  start: string;
  end: string;
  description?: string;
}): Promise<{ id: string; htmlLink: string }> => {
  try {
    const { data } = await network.put<{ data: { id: string; htmlLink: string } }>(`/google/events/${eventId}`, payload);
    return data.data;
  } catch (error) { throw new ErrorResponse(error); }
};

const deleteEvent = async (eventId: string): Promise<void> => {
  try {
    await network.delete(`/google/events/${eventId}`);
  } catch (error) { throw new ErrorResponse(error); }
};

// Externes (Google-)Event im eigenen Kalender verschieben/resizen
// (kalender-external-edit): Schreibweg Google events.patch (nur Start/Ende),
// Cache folgt nach Google-Erfolg. `id` = external_calendar_events-Zeile.
const moveExternalEvent = async (id: number, payload: {
  starts_at: string;
  ends_at: string;
}): Promise<ExternalCalendarEvent> => {
  try {
    const { data } = await network.patch<{ data: ExternalCalendarEvent }>(`/external-events/${id}`, payload);
    return data.data;
  } catch (error) { throw new ErrorResponse(error); }
};

const googleApi = {
  getAuthUrl,
  getStatus,
  getEvents,
  getFreeBusy,
  createEvent,
  updateEvent,
  deleteEvent,
  disconnect,
  getTeamConnected,
  getTeamEvents,
  triggerSync,
  getExternalEvents,
  moveExternalEvent,
};

export default googleApi;
